/**
 * Typed client for the Learning Coach (`/api/coach`): ranked suggestions with their reasons,
 * cited answers researched in WorkSim's own material, and the learner model the coach keeps,
 * which the learner can see, correct, rebuild and reset.
 *
 * Shapes mirror `packages/server/src/routes/coach.ts`, `services/coach.ts` and
 * `LearnerModelService.view` in `services/learner.ts`. The coach never changes scores, evidence or
 * mastery; every response carries the notice that says so.
 */
import { request } from './client';
import type {
  CoachAlternative,
  CoachRuleId,
  CoachSuggestion,
  CoachSuggestionKind,
  Explanation,
  LearnerConfidence,
  LearnerGoals,
  LearnerHabits,
  LearnerKindStats,
  LearnerPattern,
  LearnerPatternKind,
  LearnerTrend,
  Passage,
  PassageSource,
  PassageSourceKind,
  SkillKnowledgeSummary,
} from '@worksim/engine';

export type {
  CoachAlternative,
  CoachRuleId,
  CoachSuggestion,
  CoachSuggestionKind,
  Explanation,
  LearnerConfidence,
  LearnerKindStats,
  LearnerPattern,
  LearnerPatternKind,
  LearnerTrend,
  Passage,
  PassageSource,
  PassageSourceKind,
};

const enc = encodeURIComponent;

/** `GET /coach/health` (public). The provider's address is never returned. */
export interface CoachHealth {
  coach: 'available';
  provider: string;
  /** True when a model may rephrase answers. */
  phrasing: boolean;
  model?: string;
  modelReady?: boolean;
  notice: string;
}

/** `GET /coach/suggestions`. */
export interface CoachSuggestionsResponse {
  suggestions: CoachSuggestion[];
  notice: string;
  modelSummary: { eventCount: number; updatedAt: string | null };
}

export type CoachFeedbackAction = 'accepted' | 'dismissed' | 'done';

/** Who wrote the text of an answer: `coach` (the deterministic composer) or the model that rephrased it. */
export type CoachPhrasedBy = 'coach' | 'ollama' | 'claude' | (string & Record<never, never>);

/** The learner's level on the skill guide an answer is about (the first skill in scope with a guide). */
export interface CoachLevelView {
  skillId: string;
  skillName: string;
  guideTitle: string;
  current: string;
  title: string;
  label: string;
}

/** `POST /coach/ask`. Citation `[n]` in `explanation.answer` is `explanation.sources[n - 1]`. */
export interface CoachAskResponse {
  questionId: number;
  explanation: Explanation;
  phrasedBy: CoachPhrasedBy;
  /** Null when no skill in scope has a guide (older servers leave it out). */
  level?: CoachLevelView | null;
  /** At Advanced and above: a question from the guide's higher levels, also appended to the answer. */
  thinkAbout?: string;
  notice: string;
}

/** `GET /coach/practice/:skillId`: review questions (prompts and options only). */
export interface CoachPracticeResponse {
  skillId: string;
  skillName: string;
  /** `guide`: answered through the guide level check; `lessons`: questions from the skill's lessons. */
  source: 'guide' | 'lessons';
  levelId?: string;
  levelTitle?: string;
  label?: string;
  questions: { id: string; prompt: string; options: string[]; lessonId?: string }[];
  answerWith: { method: 'POST'; path: string };
  notice: string;
}

export interface CoachAskOptions {
  skillIds?: string[];
  /** The lesson or knowledge topic being read. */
  lessonId?: string;
  /** A knowledge-check question (`ownerId#questionId` or a plain id). */
  questionId?: string;
}

/** A wrong option the learner chose, once per question and option. */
export interface LearnerMisconceptionView {
  /** Pass back to dismiss it. */
  key: string;
  skillIds: string[];
  skillNames: string[];
  sourceId: string;
  sourceTitle: string;
  itemId: string;
  chosen: string;
  whyWrong: string;
  count: number;
  lastAt: string;
}

export type LearnerKnowledgeRow = SkillKnowledgeSummary & { name: string; observations: number };

/** `GET /coach/profile`: what the coach has learned, with the numbers behind it. */
export interface LearnerProfileView {
  knowledge: LearnerKnowledgeRow[];
  patterns: LearnerPattern[];
  habits: Omit<LearnerHabits, 'counts'>;
  goals: LearnerGoals & { careerName?: string; certificationNames: string[] };
  misconceptions: LearnerMisconceptionView[];
  feedback: { accepted: Record<string, number>; dismissed: Record<string, number>; rejectedPatterns: number };
  eventCount: number;
  updatedAt: string | null;
  notice: string;
}

export interface CoachGoalsPatch {
  careerId?: string | null;
  certificationIds?: string[];
  minutesPerDay?: number | null;
}

export const coach = {
  health: () => request<CoachHealth>('GET', '/coach/health'),
  /** Up to five suggestions. `minutes` drops anything longer; omitted, the learner's daily goal applies. */
  suggestions: (opts: { career?: string; minutes?: number } = {}) => request<CoachSuggestionsResponse>('GET', '/coach/suggestions', undefined, { career: opts.career, minutes: opts.minutes }),
  /** `accepted` and `dismissed` tune the rule for this learner; `dismissed` and `done` hide the suggestion for a week. */
  feedback: (suggestion: Pick<CoachSuggestion, 'id' | 'ruleId'>, action: CoachFeedbackAction) =>
    request<{ ok: true; ruleId: string; action: CoachFeedbackAction }>('POST', `/coach/suggestions/${enc(suggestion.id)}/feedback`, { ruleId: suggestion.ruleId, action }),
  ask: (query: string, opts: CoachAskOptions = {}) => request<CoachAskResponse>('POST', '/coach/ask', { query, ...opts }),
  /** Review questions for one skill from the learner's guide level, or from its lessons. */
  practice: (skillId: string) => request<CoachPracticeResponse>('GET', `/coach/practice/${enc(skillId)}`),
  rate: (questionId: number, helpful: boolean) => request<{ ok: true }>('POST', `/coach/questions/${questionId}/rating`, { helpful }),
  profile: () => request<LearnerProfileView>('GET', '/coach/profile'),
  rejectPattern: (id: string) => request<LearnerProfileView>('POST', `/coach/profile/patterns/${enc(id)}/reject`),
  dismissMisconception: (key: string) => request<LearnerProfileView>('POST', '/coach/profile/misconceptions/dismiss', { key }),
  setGoals: (goals: CoachGoalsPatch) => request<LearnerProfileView>('PUT', '/coach/profile/goals', goals),
  rebuild: () => request<LearnerProfileView>('POST', '/coach/profile/rebuild'),
  reset: () => request<LearnerProfileView>('DELETE', '/coach/profile'),
};
